import React, { useState, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useFilteredOrders, useOrderCounters, useFilteredOrderCounters, type OrderCategory, type OrderSubTab, CATEGORY_CONFIG } from '../../shared/store/ordersStore'
import OrderCard from '../../shared/components/OrderCard'
import { LoadingSpinner, OrdersListSkeleton } from '../../shared/components/LoadingSpinner'
import FloatingNavigation from '../../shared/components/FloatingNavigation'
import OrderFilters from '../../shared/components/OrderFilters'
import { useOrderFiltersState } from '../../shared/hooks/useOrderFilters'
import '../../shared/styles/filters.css'
import type { OrderFiltersState } from '../../types/filters'

// Используем тип Order из store
import type { Order } from '../../shared/store/ordersStore'

interface OrderCounts {
  new: number
  watching: number
  calculating: number
  price_given: number
  pending_confirmation: number
  executing: number
  defect: number
  rejected: number
  archived: number
  completed: number
}

/**
 * 🚀 Страница заказов с глобальным кешем
 * Архитектура "Загрузил один раз - пользуйся всегда"
 * ⚡ Мгновенные переключения разделов без запросов к серверу
 */

export const OrdersPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Состояние из URL параметров
  const activeCategory = (searchParams.get('category') as OrderCategory) || 'catalog'
  const activeSubTab = (searchParams.get('subTab') as OrderSubTab) || 'new'
  const searchQuery = searchParams.get('q') || ''
  
  // Локальное состояние
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  
  // Фильтры заказов с callback для обновления
  const {
    filtersState,
    toggleProcessingType,
    toggleExcludedType,
    updateFilters,
  } = useOrderFiltersState()
  
  // Обработчик изменения сортировки
  const handleSortChange = useCallback((sort: 'newest' | 'oldest') => {
    updateFilters({ selectedSort: sort })
  }, [updateFilters])
  
  // Обработчик сброса фильтров
  const handleResetFilters = useCallback(() => {
    updateFilters({ 
      selectedProcessingTypes: [],
      selectedSort: 'newest'
    })
  }, [updateFilters])
  const selectedProcessingTypes = filtersState.selectedProcessingTypes
  const excludedTypes = filtersState.excludedTypes

  // 🔧 Параметры для фильтрации - фильтры по типам обработки только для каталога!
  const filterParams = useMemo(() => {
    // Для каталога применяем все фильтры
    if (activeCategory === 'catalog') {
      return {
        processing_types: selectedProcessingTypes.length > 0 ? selectedProcessingTypes : undefined,
        excluded_types: excludedTypes.length > 0 ? excludedTypes : undefined,
        search: searchQuery || undefined,
        sort: filtersState.selectedSort,
      }
    }
    
    // Для других разделов применяем только поиск и сортировку (БЕЗ типов обработки)
    return {
      search: searchQuery || undefined,
      sort: filtersState.selectedSort,
    }
  }, [activeCategory, selectedProcessingTypes, excludedTypes, searchQuery, filtersState.selectedSort])

  // 🚀 Мгновенная фильтрация из глобального кеша
  const { 
    orders, 
    isLoading: ordersLoading, 
    error: ordersError,
    total: ordersTotal
  } = useFilteredOrders(activeCategory, activeSubTab, filterParams)

  // Обычные счетчики для всех разделов кроме каталога
  const { 
    counts: globalCounts, 
    isLoading: countersLoading,
    error: countersError 
  } = useOrderCounters()
  
  // Отфильтрованные счетчики для каталога (только если есть фильтры)
  const catalogFilterParams = activeCategory === 'catalog' ? {
    processing_types: selectedProcessingTypes.length > 0 ? selectedProcessingTypes : undefined,
    excluded_types: excludedTypes.length > 0 ? excludedTypes : undefined,
    search: searchQuery || undefined,
  } : undefined
  
  const { 
    counts: filteredCounts 
  } = useFilteredOrderCounters(catalogFilterParams)
  
  // Используем отфильтрованные счетчики для каталога, обычные для остальных
  const counts = activeCategory === 'catalog' ? filteredCounts : globalCounts

  // Подготовка данных для отображения
  const isLoading = ordersLoading || countersLoading
  const error = ordersError || countersError

  // Данные для плавающей навигации с использованием CATEGORY_CONFIG
  const navigationData = useMemo(() => 
    Object.entries(CATEGORY_CONFIG).map(([key, config]) => ({
      key: key as OrderCategory,
      title: config.title,
      icon: config.icon,
      count: config.subTabs.reduce((sum, subTab) => 
        sum + (counts[subTab.key as keyof typeof counts] || 0), 0
      ),
      isActive: activeCategory === key,
      subTabs: config.subTabs.map(subTab => ({
        key: subTab.key as OrderSubTab,
        title: subTab.title,
        icon: subTab.icon,
        count: counts[subTab.key as keyof typeof counts] || 0,
        isActive: activeSubTab === subTab.key,
      }))
    })), 
    [activeCategory, activeSubTab, counts]
  )

  // Обработчики событий
  const handleCategoryChange = useCallback((category: OrderCategory) => {
    const firstSubTab = CATEGORY_CONFIG[category].subTabs[0].key as OrderSubTab
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev)
      newParams.set('category', category)
      newParams.set('subTab', firstSubTab)
      return newParams
    })
  }, [setSearchParams])

  const handleSubTabChange = useCallback((subTab: OrderSubTab) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev)
      newParams.set('subTab', subTab)
      return newParams
    })
  }, [setSearchParams])

  const handleSearch = useCallback((query: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev)
      if (query) {
        newParams.set('q', query)
      } else {
        newParams.delete('q')
      }
      return newParams
    })
  }, [setSearchParams])

  const handleOrderClick = useCallback((orderId: string) => {
    navigate(`/orders/${orderId}`)
  }, [navigate])

  // Извлекаем уникальные типы обработки из текущих заказов для фильтров
  const availableProcessingTypes = useMemo(() => {
    const typesMap = new Map()
    
    orders.forEach(order => {
      if (order.processing_types && order.processing_types.length > 0) {
        order.processing_types.forEach(type => {
          // processing_types - это массив объектов {id, name}
          const typeName = type.name
          if (!typesMap.has(typeName)) {
            typesMap.set(typeName, {
              id: String(type.id),
              name: typeName,
              value: typeName
            })
          }
        })
      }
    })
    
    return Array.from(typesMap.values())
  }, [orders])

  // Функция форматирования даты
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    
    if (diffInHours < 1) {
      return 'Только что'
    } else if (diffInHours < 24) {
      return `${diffInHours} ч. назад`
    } else if (diffInHours < 48) {
      return 'Вчера'
    } else {
    return date.toLocaleDateString('ru-RU', {
        day: 'numeric', 
        month: 'short' 
      })
    }
  }

  // Активная категория с подвкладками
  const currentCategory = useMemo(() => {
    const config = CATEGORY_CONFIG[activeCategory]
    if (!config) return null
    
    return {
      key: activeCategory,
      title: config.title,
      icon: config.icon,
      subTabs: config.subTabs.map(subTab => ({
        key: subTab.key as OrderSubTab,
        label: subTab.title,
        count: counts[subTab.key as keyof typeof counts] || 0
      }))
    }
  }, [activeCategory, counts])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">

      {/* Фильтры - только для каталога */}
      {activeCategory === 'catalog' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <OrderFilters
            availableProcessingTypes={availableProcessingTypes}
            // Передаем состояние фильтров из родительского компонента
            filtersState={filtersState}
            onToggleProcessingType={toggleProcessingType}
            onToggleExcludedType={toggleExcludedType}
            onSortChange={handleSortChange}
            onUpdateFilters={updateFilters}
            onResetFilters={handleResetFilters}
            onFiltersChange={() => {}}
            onOrdersUpdate={() => {}}
            onError={() => {}}
          />
        </div>
      )}

      {/* Подвкладки */}
      {currentCategory && currentCategory.subTabs.length > 1 && (
        <div className="bg-white/40 backdrop-blur-sm border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-2 overflow-x-auto scrollbar-hide py-3">
              {currentCategory.subTabs.map(subTab => (
                <button
                  key={subTab.key}
                  onClick={() => handleSubTabChange(subTab.key)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeSubTab === subTab.key
                      ? 'bg-white text-blue-600 shadow-md border border-blue-100'
                      : 'text-gray-600 hover:bg-white/50 hover:text-gray-900'
                  }`}
                >
                  <span>{subTab.label}</span>
                  {subTab.count > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      activeSubTab === subTab.key
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-red-500 text-white'
                    }`}>
                      {subTab.count > 99 ? '99+' : subTab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Плавающая навигация */}
      <FloatingNavigation
        tabs={[
          {
            id: 'catalog',
            label: 'Каталог',
            icon: <ion-icon name="file-tray-full"></ion-icon>,
            // Для каталога используем отфильтрованные счетчики, если активен каталог
            count: activeCategory === 'catalog' ? (filteredCounts.new || 0) : (globalCounts.new || 0),
            isActive: activeCategory === 'catalog'
          },
          {
            id: 'views',
            label: 'Просмотры',
            icon: <ion-icon name="eye"></ion-icon>,
            // Для других разделов всегда используем глобальные счетчики (без фильтров)
            count: (globalCounts.watching || 0) + (globalCounts.calculating || 0) + (globalCounts.price_given || 0),
            isActive: activeCategory === 'views'
          },
          {
            id: 'inwork',
            label: 'В работе',
            icon: <ion-icon name="build"></ion-icon>,
            // Для других разделов всегда используем глобальные счетчики (без фильтров)
            count: (globalCounts.executing || 0) + (globalCounts.defect || 0),
            isActive: activeCategory === 'inwork'
          },
          {
            id: 'archive',
            label: 'Архив',
            icon: <ion-icon name="folder"></ion-icon>,
            // Для других разделов всегда используем глобальные счетчики (без фильтров)
            count: (globalCounts.rejected || 0) + (globalCounts.archived || 0) + (globalCounts.completed || 0),
            isActive: activeCategory === 'archive'
          }
        ]}
        onTabClick={(tabId) => {
          // Маппинг старых ключей на новые
          const categoryMap: Record<string, OrderCategory> = {
            'catalog': 'catalog',
            'views': 'views', 
            'inwork': 'inwork',
            'archive': 'archive'
          }
          handleCategoryChange(categoryMap[tabId] || tabId as OrderCategory)
        }}
      />

      {/* Пустое состояние на всю ширину экрана */}
      {!isLoading && !error && orders.length === 0 && (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-sm">
              <span className="text-4xl">📭</span>
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              {searchQuery ? 'Заказы не найдены' : 'Заказов нет'}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchQuery
                ? `По запросу "${searchQuery}" ничего не найдено`
                : 'В данной категории пока нет заказов'
              }
            </p>
            {searchQuery && (
              <button
                onClick={() => handleSearch('')}
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
              >
                Очистить поиск
              </button>
            )}
          </div>
        </div>
      )}

      {/* Основной контент */}
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-4 pb-24 md:pb-32 ${
        activeCategory === 'catalog' ? 'pt-4' : 'py-8'
      }`}>
        {/* Состояние загрузки */} 
        {isLoading && (
          <OrdersListSkeleton viewMode={viewMode} count={6} />
        )}

        {error && !isLoading && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Список заказов */}
        {!isLoading && !error && orders.length > 0 && (
          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
            {orders.map(order => (
              <OrderCard
                key={order.id}
                order={order}
                viewMode={viewMode}
                onClick={handleOrderClick}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}
      </div>

      {/* Дополнительные стили */}
      <style>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  )
}
